"use client";

import { useState, useMemo, useEffect } from "react";
import { useApp } from "@/context/AppContext";
import { useSubscriptions } from "@/context/SubscriptionContext";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Subscription, Appointment } from "@/types";
import { Subscription as SubscriptionPlan } from "@/types/subscription.types";
import { subscriptionService } from "@/services/subscriptionService";
import { userSubscriptionService } from "@/services/userSubscriptionService";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import CommonHeader from "./common/CommonHeader";
import { SubscriptionsTable } from "./subscription/SubscriptionsTable";
import { SubscriptionDetailDialog } from "./subscription/SubscriptionDetailDialog";
import { SubscriptionConfirmDialog } from "./subscription/SubscriptionConfirmDialog";
import { AddSubscriptionDialog } from "./subscription/AddSubscriptionDialog";
import { EditSubscriptionDialog } from "./subscription/EditSubscriptionDialog";
import {
  SubscriptionFilter,
  SubscriptionFilters,
} from "./subscription/SubscriptionFilter";

/**
 * Calculate remaining services for a subscription based on appointments
 * Counts individual services (exterior, interior, dash polish) from servicesCompleted field
 */
function calculateSubscriptionServices(
  subscription: Subscription,
  appointments: Appointment[],
): {
  exteriorCleaningsUsed: number;
  interiorCleaningsUsed: number;
  dashPolishUsed: number;
  remainingServices: number;
  totalServices: number;
} {
  // Filter appointments linked to this subscription
  const subscriptionAppointments = appointments.filter(
    (apt) => apt.type === "USER_SUBSCRIPTION" && apt.typeId === subscription.id,
  );

  // Count services used from servicesCompleted field
  let exteriorCleaningsUsed = 0;
  let interiorCleaningsUsed = 0;
  let dashPolishUsed = 0;

  subscriptionAppointments.forEach((apt) => {
    if (apt.servicesCompleted) {
      if (apt.servicesCompleted.exteriorCleaning === true) {
        exteriorCleaningsUsed++;
      }
      if (apt.servicesCompleted.interiorCleaning === true) {
        interiorCleaningsUsed++;
      }
      if (apt.servicesCompleted.dashPolish === true) {
        dashPolishUsed++;
      }
    }
  });

  // Get totals from subscription (these come from the plan features)
  const exteriorCleaningsTotal = subscription.exteriorCleaningsTotal ?? 0;
  const interiorCleaningsTotal = subscription.interiorCleaningsTotal ?? 0;
  const dashPolishTotal = subscription.dashPolishTotal ?? 0;

  // Calculate remaining services
  const exteriorRemaining = Math.max(
    0,
    exteriorCleaningsTotal - exteriorCleaningsUsed,
  );
  const interiorRemaining = Math.max(
    0,
    interiorCleaningsTotal - interiorCleaningsUsed,
  );
  const dashPolishRemaining = Math.max(0, dashPolishTotal - dashPolishUsed);

  // Calculate totals
  const totalServices =
    exteriorCleaningsTotal + interiorCleaningsTotal + dashPolishTotal;
  const remainingServices =
    exteriorRemaining + interiorRemaining + dashPolishRemaining;

  return {
    exteriorCleaningsUsed,
    interiorCleaningsUsed,
    dashPolishUsed,
    remainingServices,
    totalServices,
  };
}

export function SubscriptionManagement() {
  const {
    subscriptions,
    customers,
    vehicles,
    appointments,
    subscriptionPlans,
    updateSubscription,
  } = useApp();

  const { createUserSubscription, refreshSubscriptions } = useSubscriptions();

  const [activeTab, setActiveTab] = useState("all");
  const [selectedSubscription, setSelectedSubscription] =
    useState<Subscription | null>(null);
  const [isDetailDialogOpen, setIsDetailDialogOpen] = useState(false);
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingSubscription, setEditingSubscription] =
    useState<Subscription | null>(null);
  const [confirmAction, setConfirmAction] = useState<
    "pause" | "resume" | "cancel" | null
  >(null);

  // Filter state
  const [filters, setFilters] = useState<SubscriptionFilters>({
    search: "",
    planId: "all",
    startDate: "",
    endDate: "",
  });

  // Calculate services for all subscriptions based on appointments
  const subscriptionsWithCalculatedServices = useMemo(() => {
    return subscriptions.map((subscription) => {
      const calculated = calculateSubscriptionServices(
        subscription,
        appointments,
      );
      // Preserve original totals from subscription plan, use calculated totals as fallback
      const exteriorTotal = subscription.exteriorCleaningsTotal || 0;
      const interiorTotal = subscription.interiorCleaningsTotal || 0;
      const dashPolishTotal = subscription.dashPolishTotal || 0;
      const totalServices =
        exteriorTotal + interiorTotal + dashPolishTotal ||
        calculated.totalServices;

      return {
        ...subscription,
        exteriorCleaningsUsed: calculated.exteriorCleaningsUsed,
        interiorCleaningsUsed: calculated.interiorCleaningsUsed,
        dashPolishUsed: calculated.dashPolishUsed,
        exteriorCleaningsTotal: exteriorTotal,
        interiorCleaningsTotal: interiorTotal,
        dashPolishTotal: dashPolishTotal,
        remainingServices: calculated.remainingServices,
        totalServices: totalServices,
      };
    });
  }, [subscriptions, appointments]);

  // Filter subscriptions based on active tab and filters
  const filteredSubscriptions = useMemo(() => {
    let result = subscriptionsWithCalculatedServices;

    // Status filter (from tabs)
    if (activeTab !== "all") {
      result = result.filter((sub) => sub.status === activeTab);
    }

    // Search filter
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      result = result.filter((sub) => {
        const customer = customers.find((c) => c.userId === sub.customerId);
        const vehicle = vehicles.find((v) => v.id === sub.vehicleId);
        return (
          customer?.name?.toLowerCase().includes(searchLower) ||
          customer?.phone?.includes(filters.search) ||
          vehicle?.plateNumber?.toLowerCase().includes(searchLower)
        );
      });
    }

    // Plan filter
    if (filters.planId && filters.planId !== "all") {
      result = result.filter((sub) => sub.subscriptionId === filters.planId);
    }

    // Date range filter (based on startDate)
    if (filters.startDate) {
      const start = new Date(filters.startDate);
      start.setHours(0, 0, 0, 0);
      result = result.filter((sub) => {
        const subStart = new Date(sub.startDate);
        return subStart >= start;
      });
    }
    if (filters.endDate) {
      const end = new Date(filters.endDate);
      end.setHours(23, 59, 59, 999);
      result = result.filter((sub) => {
        const subStart = new Date(sub.startDate);
        return subStart <= end;
      });
    }

    return result;
  }, [
    subscriptionsWithCalculatedServices,
    activeTab,
    filters,
    customers,
    vehicles,
  ]);

  const getStatusBadge = (status: string) => {
    const colors: Record<string, string> = {
      active: "bg-green-500 hover:bg-green-600",
      paused: "bg-yellow-500 hover:bg-yellow-600",
      cancelled: "bg-red-500 hover:bg-red-600",
      expired: "bg-gray-500 hover:bg-gray-600",
    };

    return (
      <Badge className={colors[status] || ""}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  const handleAction = (
    subscription: Subscription,
    action: "pause" | "resume" | "cancel",
  ) => {
    setSelectedSubscription(subscription);
    setConfirmAction(action);
    setIsConfirmDialogOpen(true);
  };

  const confirmActionHandler = async () => {
    if (!selectedSubscription || !confirmAction) return;

    try {
      if (confirmAction === "cancel") {
        // Use DELETE endpoint to cancel subscription
        await userSubscriptionService.deleteUserSubscription(
          selectedSubscription.id,
        );
      } else {
        // Pause or Resume - fetch current subscription first to preserve details and features
        const currentSubscription =
          await userSubscriptionService.getUserSubscriptionById(
            selectedSubscription.id,
          );
        const isActive = confirmAction === "resume";

        // Transform rules to details format
        const details = currentSubscription.rules.map((rule) => ({
          id: rule.id,
          day: rule.day,
          time: rule.time,
          vehicleId: rule.vehicleId,
        }));

        await userSubscriptionService.updateUserSubscription(
          selectedSubscription.id,
          {
            isActive,
            subscriptionId: currentSubscription.subscriptionId,
            details,
            // features: currentSubscription.features,
          },
        );
      }

      // Refresh subscriptions to get updated data from backend
      await refreshSubscriptions();
      toast.success(`Subscription ${confirmAction}d successfully`);
      setIsConfirmDialogOpen(false);
      setConfirmAction(null);
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        `Failed to ${confirmAction} subscription`;
      console.error(`Error ${confirmAction}ing subscription:`, error);
      toast.error(errorMessage);
    }
  };

  const viewDetails = (subscription: Subscription) => {
    // Find the subscription with calculated services
    const calculatedSubscription =
      subscriptionsWithCalculatedServices.find(
        (sub) => sub.id === subscription.id,
      ) || subscription;
    setSelectedSubscription(calculatedSubscription);
    setIsDetailDialogOpen(true);
  };

  const handleEdit = (subscription: Subscription) => {
    // Find the subscription with calculated services
    const calculatedSubscription =
      subscriptionsWithCalculatedServices.find(
        (sub) => sub.id === subscription.id,
      ) || subscription;
    setEditingSubscription(calculatedSubscription);
    setIsEditDialogOpen(true);
  };

  const handleCreateSubscription = async (
    formData: any,
    planFeatures?: any,
  ) => {
    try {
      await createUserSubscription(formData, planFeatures);
      await refreshSubscriptions();
      setIsAddDialogOpen(false);
    } catch (error) {
      // Error is already handled in createUserSubscription
    }
  };

  // Get subscription plans from API (these are the templates)
  const [subscriptionPlanTemplates, setSubscriptionPlanTemplates] = useState<
    SubscriptionPlan[]
  >([]);

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const plans = await subscriptionService.getAllSubscriptions();
        setSubscriptionPlanTemplates(plans);
      } catch (error) {
        console.error("Error fetching subscription plans:", error);
      }
    };
    fetchPlans();
  }, []);

  return (
    <div className="space-y-6 w-full overflow-x-hidden">
      {/* Header */}
      <CommonHeader
        title="Subscription Management"
        description="Manage recurring subscriptions"
        onClick={() => setIsAddDialogOpen(true)}
        buttonText="Add Subscription"
        buttonIcon={Plus}
      />

      {/* Filters */}
      <SubscriptionFilter
        filters={filters}
        onFilterChange={setFilters}
        plans={subscriptionPlanTemplates}
      />

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="all">All</TabsTrigger>
          <TabsTrigger value="active">Active</TabsTrigger>
          <TabsTrigger value="paused">Paused</TabsTrigger>
          <TabsTrigger value="cancelled">Cancelled</TabsTrigger>
          <TabsTrigger value="expired">Expired</TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab} className="mt-4">
          {/* Subscriptions Table */}
          <SubscriptionsTable
            subscriptions={filteredSubscriptions}
            customers={customers}
            vehicles={vehicles}
            getStatusBadge={getStatusBadge}
            onView={viewDetails}
            onEdit={handleEdit}
            onPause={(sub) => handleAction(sub, "pause")}
            onResume={(sub) => handleAction(sub, "resume")}
            onCancel={(sub) => handleAction(sub, "cancel")}
          />
        </TabsContent>
      </Tabs>

      {/* Add Subscription Dialog */}
      <AddSubscriptionDialog
        open={isAddDialogOpen}
        onOpenChange={setIsAddDialogOpen}
        customers={customers}
        vehicles={vehicles}
        subscriptionPlans={subscriptionPlanTemplates}
        onCreate={handleCreateSubscription}
      />

      {/* Detail Dialog */}
      <SubscriptionDetailDialog
        open={isDetailDialogOpen}
        onOpenChange={setIsDetailDialogOpen}
        subscription={selectedSubscription}
        customers={customers}
        vehicles={vehicles}
        appointments={appointments}
        getStatusBadge={getStatusBadge}
        onEdit={handleEdit}
      />

      {/* Confirm Action Dialog */}
      <SubscriptionConfirmDialog
        open={isConfirmDialogOpen}
        onOpenChange={setIsConfirmDialogOpen}
        action={confirmAction}
        onConfirm={confirmActionHandler}
      />

      {/* Edit Subscription Dialog */}
      <EditSubscriptionDialog
        open={isEditDialogOpen}
        onOpenChange={setIsEditDialogOpen}
        subscription={editingSubscription}
        vehicles={vehicles}
      />
    </div>
  );
}
